import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { createMuiTheme, ThemeProvider } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Validation } from '../../../model/validation/validation';
import './date-picker.scss';
import { UserAgent } from '../../../utilities/user-agent';
import { Required } from '../required/required';
import { DateFormatter } from '../../../utilities/date-formatter';
import { ErrorPop } from '../error-pop/error-pop';
import { ValidationNotEmpty } from '../../../model/validation/validation-not-empty';
import { ValidationDatePicker } from '../../../model/validation/validation-date-picker';

type DateProps = {
  type?: 'YYYY/MM' | 'YYYY/MM/DD',
  date: Date | null,
  onChange: (v: Date | null) => void,
  // dateFormat?: DateFormat,
  disabled?: boolean,
  /**  触る前からエラー出すかどうか */
  touch?: boolean;
  /** バリデーション */
  validationList?: Validation[],
  /** エラーメッセージの表示位置を下に表示する場合 */
  errorPosBottom?: boolean,
  minDate?: string;
  label?: string;
  require?: boolean;
  term?: string[];
  /** 任意文字列の場合string Valueの場合true */
  title?: string | boolean;
  noRequireLabel?: boolean;
};

/* Material UI 用 */
const defaultMaterialTheme = createMuiTheme({});

export const DatePicker = (props: DateProps) => {
  const {
    date,
    onChange,
    disabled,
    touch,
    validationList: _validationList,
    errorPosBottom,
    minDate,
    require,
    term,
    label,
    title,
    noRequireLabel,
    type,
  } = props;

  /* State */
  const [changed, setChanged] = React.useState<boolean>(false);
  const [errorMessageList, setErrorMessageList] = useState<string[]>([]);
  const [focus, setFocus] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  /* Memo */
  const tip = useMemo(() => {
    if (!title) return undefined;
    if (typeof title === 'string') {
      return title;
    }
    return DateFormatter.date2str(date) || '';
  }, [title, date]);
  const validationList = useMemo(() => [
    ..._validationList || [],
    ...ValidationDatePicker,
  ], [_validationList]);

  /* Callback */
  const changeVal = useCallback(
    (value: MaterialUiPickersDate) => {
      setChanged(true);
      setFocus(false);
      onChange(value);
      if (!validationList) {
        setErrorMessageList(
          require ? ValidationNotEmpty
            .filter((v) => !v.run(value ? DateFormatter.date2str(value) : ''))
            .map((v) => v.errorMessage)
            : [],
        );
      } else {
        setErrorMessageList(
          require ? [...ValidationNotEmpty, ...validationList]
            .filter((v) => !v.run(value ? DateFormatter.date2str(value) : ''))
            .map((v) => v.errorMessage)
            : validationList
              .filter((v) => !v.run(value ? DateFormatter.date2str(value) : ''))
              .map((v) => v.errorMessage),
        );
      }
    }, [onChange, validationList, require],
  );

  /* Effect */
  useEffect(
    () => {
      if (touch) {
        if (!validationList) {
          setErrorMessageList(
            require ? ValidationNotEmpty
              .filter((v) => !v.run(date ? DateFormatter.date2str(date) : ''))
              .map((v) => v.errorMessage)
              : [],
          );
        } else {
          setErrorMessageList(
            require ? [...ValidationNotEmpty, ...validationList]
              .filter((v) => !v.run(date ? DateFormatter.date2str(date) : ''))
              .map((v) => v.errorMessage)
              : validationList
                .filter((v) => !v || !v.run(date ? DateFormatter.date2str(date) : ''))
                .map((v) => v.errorMessage),
          );
        }
      }
    },
    [touch],
  );

  const error = useMemo(() => Boolean((touch || changed) && errorMessageList.length),
    [touch, changed, errorMessageList]);
  const baseClassName = 'DatePicker';
  const defaultClass = useMemo(() => (UserAgent === 'sp' ? `${baseClassName}-sp` : `${baseClassName}-pc`),
    [UserAgent]);

  const errorMessage: string[] = useMemo<string[]>(() => {
    if (error) {
      return errorMessageList;
    }
    if (term?.length) {
      return ['正しい日付範囲を入力してください'];
    }

    return [];
  }, [term, error, errorMessageList]);

  const handleOnBlur = useCallback(() => {
    setChanged(true);
    setFocus(false);
  },
  [date]);

  return (
    <div
      title={tip}
      onFocus={() => setFocus(true)}
      onBlur={handleOnBlur}
      className={
        `datePicker
        ${disabled ? ' disabled' : ('')}
        ${error ? ' InputTextError' : ('')}
        ${(!error && term?.length) ? ' InputTextError' : ('')}
        ${UserAgent === 'pc' ? ' error_focus' : ''}
        `
      }
    >
      {/* {error && (UserAgent === 'sp' ? focus : true) && (
      <ErrorPop messages={errorMessageList} errorPosBottom={errorPosBottom} /> */}
      {(error || !!term?.length) && (UserAgent === 'sp' ? focus : true) && (
      <ErrorPop messages={errorMessage} errorPosBottom={errorPosBottom} />
      )}

      <label className={`${defaultClass}__label`}>{label}{require && !noRequireLabel && <Required />}</label>
      <ThemeProvider theme={defaultMaterialTheme}>
        <KeyboardDatePicker
          onClick={() => setOpen(true)}
          onClose={() => setOpen(false)}
          open={open}
          views={type === 'YYYY/MM' ? ['year', 'month'] : undefined}
          className={error && (UserAgent === 'sp' ? focus : true) ? 'error' : ''}
          value={date}
          inputVariant="outlined"
          autoOk
          onChange={(e) => {
            setChanged(true);
            changeVal(e);
          }}
          InputProps={{
            readOnly: true,
          }}
          onBlur={() => setFocus(false)}
          label={undefined}
          error={error}
          disabled={disabled}
          clearable
          clearLabel="クリア"
          cancelLabel="キャンセル"
          maxDateMessage={null}
          minDateMessage={null}
          invalidDateMessage={null}
          minDate={minDate || '1900-01-01'}
          inputMode="numeric"
          type="tel"
          format={type === 'YYYY/MM' ? 'yyyy/MM' : 'yyyy/MM/dd'}
          InputAdornmentProps={{ position: 'end' }}
        />
      </ThemeProvider>
    </div>
  );
};
